import React, { useState, useEffect } from 'react';
import { Task, TaskComment, TaskStatus, BusinessUnit, TaskPriority, UserRole } from '../types/task';
import { getTaskService } from '../services/sharepointHelpers';
import { getAuthService } from '../services/authService';
import TaskCommentThread from './TaskCommentThread';
import SearchableDropdown from './SearchableDropdown';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Badge,
  Input,
  Label,
  Select,
  Textarea,
  Spinner,
  MessageBar,
  Portal,
  mergeClasses,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  EditRegular,
  DeleteRegular,
  DismissRegular,
  PersonRegular,
  CalendarRegular,
  AlertRegular,
  TagRegular,
  BuildingRegular,
  ChatRegular,
  SaveRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    width: '98%',
    maxWidth: '1400px',
    maxHeight: '90vh',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow64,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  header: {
    padding: '16px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '20px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.3',
  },
  urgentBadge: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    padding: '4px 12px',
    borderRadius: '16px',
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px',
    maxHeight: 'calc(90vh - 100px)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  statCard: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow8,
      transform: 'translateY(-2px)',
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
  },
  statCardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: tokens.colorBrandStroke1,
  },
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: '20px',
    flexShrink: 0,
  },
  statLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginTop: '4px',
  },
  statValue: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2',
  },
  contentGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '20px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  detailsSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '14px',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '6px',
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
    padding: '10px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    transition: 'all 0.2s ease',
  },
  descriptionField: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.7',
    padding: '20px 24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '120px',
    width: '100%',
    boxShadow: tokens.shadow2,
  },
  fieldValueWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
    padding: '10px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    transition: 'all 0.2s ease',
  },
  fieldIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '18px',
  },
  fieldValueSecondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
    paddingLeft: '16px',
  },
  badgeContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    minHeight: '44px',
  },
  tag: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  overdue: {
    color: tokens.colorPaletteRedForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  actions: {
    padding: '20px 32px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  commentsSection: {
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: '16px',
    marginTop: '16px',
  },
  commentsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '20px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    marginTop: '20px',
  },
  statusBadge: {
    '&.open': {
      backgroundColor: tokens.colorPaletteBlueBackground2,
      color: tokens.colorPaletteBlueForeground2,
    },
    '&.in-progress': {
      backgroundColor: tokens.colorPaletteYellowBackground2,
      color: tokens.colorPaletteYellowForeground2,
    },
    '&.complete': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
      color: tokens.colorPaletteGreenForeground2,
    },
    '&.on-hold': {
      backgroundColor: tokens.colorNeutralBackground3,
      color: tokens.colorNeutralForeground3,
    },
    '&.cancelled': {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground2,
    },
  },
  priorityBadge: {
    '&.urgent': {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground2,
    },
    '&.high': {
      backgroundColor: tokens.colorPaletteDarkOrangeBackground2,
      color: tokens.colorPaletteDarkOrangeForeground2,
    },
    '&.medium': {
      backgroundColor: tokens.colorPaletteYellowBackground2,
      color: tokens.colorPaletteYellowForeground2,
    },
    '&.low': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
      color: tokens.colorPaletteGreenForeground2,
    },
  },
  badge: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    padding: '6px 12px',
    borderRadius: '12px',
  },
  keyField: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: tokens.shadow4,
  },
  sectionCard: {
    padding: '28px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
    transition: 'all 0.2s ease',
  },

});

interface TaskDetailsModalProps {
  task: Task;
  onClose: () => void;
  onUpdate: (taskId: string, updates: Partial<Task>) => void;
  onDelete: (taskId: string) => void;
  userRole: UserRole;
  userId: string;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  task,
  onClose,
  onUpdate,
  onDelete,
  userRole,
  userId
}) => {
  const styles = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState<Task>(task);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string; email: string; role: string; businessUnit?: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taskService = getTaskService(getAuthService());

  // Load comments
  useEffect(() => {
    const loadComments = async () => {
      try {
        setLoading(true);
        const taskComments = await taskService.getTaskComments(task.id);
        setComments(taskComments);
      } catch (error) {
        console.error('Error loading comments:', error);
        setError('Failed to load comments');
      } finally {
        setLoading(false);
      }
    };

    loadComments();
  }, [task.id, taskService]);

  // Load users for assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await taskService.getUsers();
        setUsers(userList);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    loadUsers();
  }, [taskService]);

  // Handle save
  const handleSave = async () => {
    try {
      console.log('🔍 TaskDetailsModal - handleSave called!');
      setSaving(true);
      setError(null);
      
      // Calculate only the changes
      const changes: Partial<Task> = {};
      if (editedTask.title !== task.title) changes.title = editedTask.title;
      if (editedTask.description !== task.description) changes.description = editedTask.description;
      if (editedTask.assignedTo !== task.assignedTo) changes.assignedTo = editedTask.assignedTo;
      if (editedTask.businessUnit !== task.businessUnit) changes.businessUnit = editedTask.businessUnit;
      if (editedTask.urgent !== task.urgent) changes.urgent = editedTask.urgent;
      if (editedTask.status !== task.status) changes.status = editedTask.status;
      if (editedTask.priority !== task.priority) changes.priority = editedTask.priority;
      if (editedTask.dueDate !== task.dueDate) changes.dueDate = editedTask.dueDate;
      if (JSON.stringify(editedTask.tags) !== JSON.stringify(task.tags)) changes.tags = editedTask.tags;
      if (editedTask.created !== task.created) changes.created = editedTask.created;
      if (editedTask.createdBy !== task.createdBy) changes.createdBy = editedTask.createdBy;
      
      console.log('🔍 TaskDetailsModal - Original task:', task);
      console.log('🔍 TaskDetailsModal - Edited task:', editedTask);
      console.log('🔍 TaskDetailsModal - Changes to send:', changes);
      
      await onUpdate(task.id, changes);
      
      // Update the editedTask to reflect the saved changes
      setEditedTask(prev => ({ ...prev, ...changes }));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating task:', error);
      setError('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditedTask(task);
    setIsEditing(false);
    setError(null);
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      onDelete(task.id);
      onClose();
    }
  };

  // Check permissions
  const canEdit = userRole === UserRole.EXECUTIVE || task.createdBy === userId || task.assignedTo === userId;
  const canDelete = userRole === UserRole.EXECUTIVE || task.createdBy === userId;



  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.OPEN:
        return { className: 'open' };
      case TaskStatus.IN_PROGRESS:
        return { className: 'in-progress' };
      case TaskStatus.COMPLETE:
        return { className: 'complete' };
      case TaskStatus.ON_HOLD:
        return { className: 'on-hold' };
      case TaskStatus.CANCELLED:
        return { className: 'cancelled' };
      default:
        return { className: 'open' };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return { className: 'urgent' };
      case 'High':
        return { className: 'high' };
      case 'Medium':
        return { className: 'medium' };
      case 'Low':
        return { className: 'low' };
      default:
        return { className: 'medium' };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = () => {
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date() && task.status !== TaskStatus.COMPLETE;
  };

  const getDaysUntilDue = () => {
    if (!task.dueDate) return null;
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Portal>
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.headerLeft}>
                <div className={styles.titleRow}>
                  <Text className={styles.title}>
                    {isEditing ? 'Edit Task' : task.title}
                  </Text>
                  {task.urgent && (
                    <Badge appearance="filled" className={styles.urgentBadge}>
                      Urgent
                    </Badge>
                  )}
                </div>
                <div className={styles.subtitle}>
                  <PersonRegular />
                  <span>
                    {isEditing ? 'Update task details' : `Created by ${task.createdBy} on ${formatDate(task.created)}`}
                  </span>
                </div>
              </div>
              <div className={styles.headerRight}>
                {canEdit && !isEditing && (
                  <Button
                    appearance="subtle"
                    icon={<EditRegular />}
                    onClick={() => {
                      console.log('🔍 TaskDetailsModal - Edit button clicked!');
                      setIsEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  onClick={onClose}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Error Display */}
            {error && (
              <MessageBar style={{ marginBottom: '12px' }}>
                {error}
              </MessageBar>
            )}

            {/* Main Content Grid */}
            <div className={styles.contentGrid}>
              {/* Task Details - Two Columns */}
              <div className={styles.detailsSection}>
                 {/* Left Column */}
                 <div className={mergeClasses(styles.section, styles.sectionCard)}>
                   <Text className={styles.sectionTitle}>
                     <DocumentRegular />
                     Task Details
                   </Text>
                   
                   {/* Title Field */}
                   <div className={styles.field}>
                     <Label htmlFor="title">Title</Label>
                     {isEditing ? (
                       <Input
                         id="title"
                         value={editedTask.title}
                         onChange={(e, data) => setEditedTask(prev => ({ ...prev, title: data.value }))}
                       />
                     ) : (
                       <Text className={styles.fieldValue}>{task.title}</Text>
                     )}
                   </div>

                   {/* Assigned To Field */}
                   <div className={styles.field}>
                     <Label htmlFor="assignedTo">Assigned To</Label>
                     {isEditing ? (
                       <SearchableDropdown
                         label=""
                         placeholder="Type to search for a volunteer, manager, or business leader..."
                         options={users}
                         value={editedTask.assignedTo}
                         onChange={(value) => setEditedTask(prev => ({ ...prev, assignedTo: value }))}
                       />
                     ) : (
                       <div className={mergeClasses(styles.fieldValueWithIcon, styles.keyField)}>
                         <PersonRegular className={styles.fieldIcon} />
                         <span>{task.assignedTo}</span>
                       </div>
                     )}
                   </div>

                   {/* Business Unit Field */}
                   <div className={styles.field}>
                     <Label htmlFor="businessUnit">Department</Label>
                     {isEditing ? (
                       <Select
                         id="businessUnit"
                         value={editedTask.businessUnit}
                         onChange={(e, data) => setEditedTask(prev => ({ ...prev, businessUnit: data.value as BusinessUnit }))}
                       >
                         {Object.values(BusinessUnit).map(unit => (
                           <option key={unit} value={unit}>{unit === 'LEHR' ? 'Legal & HR (LEHR)' : unit}</option>
                          ))}
                        </Select>
                      ) : (
                        <div className={mergeClasses(styles.fieldValueWithIcon, styles.keyField)}>
                          <BuildingRegular className={styles.fieldIcon} />
                          <span>{task.businessUnit === 'LEHR' ? 'Legal & HR (LEHR)' : task.businessUnit}</span>
                        </div>
                      )}
                    </div>

                    {/* Urgent Field */}
                    <div className={styles.field}>
                      <Label htmlFor="urgent">Urgent</Label>
                      {isEditing ? (
                        <Select
                          id="urgent"
                          value={editedTask.urgent ? 'true' : 'false'}
                          onChange={(e, data) => setEditedTask(prev => ({ ...prev, urgent: data.value === 'true' }))}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </Select>
                      ) : (
                        <div className={mergeClasses(styles.fieldValueWithIcon, task.urgent ? styles.keyField : '')}>
                          <AlertRegular className={styles.fieldIcon} />
                          <span>{task.urgent ? 'Yes' : 'No'}</span>
                        </div>
                      )}
                    </div>

                    {/* Tags Field */}
                    <div className={styles.field}>
                      <Label htmlFor="tags">Tags</Label>
                      {isEditing ? (
                        <Input
                          id="tags"
                          value={editedTask.tags?.join(', ') || ''}
                          onChange={(e, data) => setEditedTask(prev => ({ 
                            ...prev, 
                            tags: data.value ? data.value.split(',').map(tag => tag.trim()).filter(tag => tag) : []
                          }))}
                          contentBefore={<TagRegular />}
                          placeholder="Enter tags separated by commas"
                        />
                      ) : (
                        <div className={styles.badgeContainer}>
                          {task.tags && task.tags.length > 0 ? (
                            task.tags.map((tag, index) => (
                              <span key={index} className={styles.tag}>
                                {tag}
                              </span>
                            ))
                          ) : (
                            <Text className={styles.fieldValueSecondary}>No tags</Text>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Attachments Field */}
                    <div className={styles.field}>
                      <Label htmlFor="attachments">Attachments</Label>
                      {isEditing ? (
                        <div style={{ 
                          padding: '12px 16px', 
                          backgroundColor: tokens.colorNeutralBackground2, 
                          borderRadius: tokens.borderRadiusMedium,
                          border: `2px solid ${tokens.colorNeutralStroke1}`,
                        }}>
                          <input
                            type="file"
                            id="attachments"
                            multiple
                            onChange={(e) => {
                              const files = e.target.files;
                              if (files && files.length > 0) {
                                // Handle file uploads - for now just show file names
                                const fileNames = Array.from(files).map(f => f.name);
                                console.log('Files selected:', fileNames);
                                // TODO: Implement file upload to SharePoint
                              }
                            }}
                            style={{ 
                              padding: '8px',
                              border: `1px solid ${tokens.colorNeutralStroke2}`,
                              borderRadius: tokens.borderRadiusSmall,
                              width: '100%',
                              backgroundColor: tokens.colorNeutralBackground1,
                              color: tokens.colorNeutralForeground1,
                              fontSize: tokens.fontSizeBase300,
                            }}
                          />
                          <Text size={200} style={{ 
                            marginTop: '8px', 
                            color: tokens.colorNeutralForeground3,
                            fontStyle: 'italic'
                          }}>
                            File upload to SharePoint coming soon
                          </Text>
                        </div>
                      ) : (
                        <div className={styles.fieldValueWithIcon}>
                          <DocumentRegular className={styles.fieldIcon} />
                          <span>{task.attachments && task.attachments.length > 0 
                            ? `${task.attachments.length} attachment(s)` 
                            : 'No attachments'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className={mergeClasses(styles.section, styles.sectionCard)}>
                    <Text className={styles.sectionTitle}>
                      <CalendarRegular />
                      Timeline & Metadata
                    </Text>
                    
                    {/* Status Field */}
                    <div className={styles.field}>
                      <Label htmlFor="status">Status</Label>
                      {isEditing ? (
                        <Select
                          id="status"
                          value={editedTask.status}
                          onChange={(e, data) => setEditedTask(prev => ({ ...prev, status: data.value as TaskStatus }))}
                        >
                          {Object.values(TaskStatus).map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </Select>
                      ) : (
                        <Badge 
                          appearance="filled" 
                          className={mergeClasses(styles.badge, styles.statusBadge, getStatusColor(task.status).className)}
                        >
                          {task.status}
                        </Badge>
                      )}
                    </div>

                    {/* Priority Field */}
                    <div className={styles.field}>
                      <Label htmlFor="priority">Priority</Label>
                      {isEditing ? (
                        <Select
                          id="priority"
                          value={editedTask.priority || TaskPriority.MEDIUM}
                          onChange={(e, data) => setEditedTask(prev => ({ ...prev, priority: data.value as TaskPriority }))}
                        >
                          {Object.values(TaskPriority).map(priority => (
                            <option key={priority} value={priority}>{priority}</option>
                          ))}
                        </Select>
                      ) : (
                        <Badge 
                          appearance="filled" 
                          className={mergeClasses(styles.badge, styles.priorityBadge, getPriorityColor(task.priority || 'Medium').className)}
                        >
                          {task.priority || 'Medium'}
                        </Badge>
                      )}
                    </div>

                    {/* Due Date Field */}
                    <div className={styles.field}>
                      <Label htmlFor="dueDate">Due Date</Label>
                      {isEditing ? (
                        <Input
                          id="dueDate"
                          type="date"
                          value={editedTask.dueDate || ''}
                          onChange={(e, data) => setEditedTask(prev => ({ ...prev, dueDate: data.value }))}
                          contentBefore={<CalendarRegular />}
                        />
                      ) : (
                        <div>
                          {task.dueDate ? (
                            <div className={mergeClasses(styles.fieldValueWithIcon, styles.keyField)}>
                              <CalendarRegular className={styles.fieldIcon} />
                              <span className={isOverdue() ? styles.overdue : ''}>
                                {formatDate(task.dueDate)}
                              </span>
                            </div>
                          ) : (
                            <Text className={styles.fieldValueSecondary}>No due date</Text>
                          )}
                          {task.dueDate && isOverdue() && (
                            <Text className={styles.overdue}>
                              Overdue by {Math.abs(getDaysUntilDue()!)} day{Math.abs(getDaysUntilDue()!) !== 1 ? 's' : ''}
                            </Text>
                          )}
                          {task.dueDate && !isOverdue() && getDaysUntilDue() !== null && getDaysUntilDue()! > 0 && (
                            <Text className={styles.fieldValueSecondary}>
                              Due in {getDaysUntilDue()} day{getDaysUntilDue() !== 1 ? 's' : ''}
                            </Text>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Created Date Field */}
                    <div className={styles.field}>
                      <Label htmlFor="created">Created Date</Label>
                      {isEditing ? (
                        <Input
                          id="created"
                          type="datetime-local"
                          value={editedTask.created ? new Date(editedTask.created).toISOString().slice(0, 16) : ''}
                          onChange={(e, data) => {
                            if (data.value) {
                              const date = new Date(data.value);
                              setEditedTask(prev => ({ ...prev, created: date.toISOString() }));
                            }
                          }}
                          contentBefore={<CalendarRegular />}
                        />
                      ) : (
                        <div className={mergeClasses(styles.fieldValueWithIcon, styles.keyField)}>
                          <CalendarRegular className={styles.fieldIcon} />
                          <span>{formatDate(task.created)}</span>
                        </div>
                      )}
                      <Text className={styles.fieldValueSecondary}>by {task.createdBy}</Text>
                    </div>

                    {/* Created By Field */}
                    <div className={styles.field}>
                      <Label htmlFor="createdBy">Created By</Label>
                      {isEditing ? (
                        <Input
                          id="createdBy"
                          value={editedTask.createdBy}
                          onChange={(e, data) => setEditedTask(prev => ({ ...prev, createdBy: data.value }))}
                          contentBefore={<PersonRegular />}
                        />
                      ) : (
                        <div className={mergeClasses(styles.fieldValueWithIcon, styles.keyField)}>
                          <PersonRegular className={styles.fieldIcon} />
                          <span>{task.createdBy}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              {/* Description Section - Full Width */}
              <div className={mergeClasses(styles.section, styles.sectionCard)}>
                <Text className={styles.sectionTitle}>
                  <DocumentRegular />
                  Description
                </Text>
                {isEditing ? (
                  <Textarea
                    id="description"
                    value={editedTask.description}
                    onChange={(e, data) => setEditedTask(prev => ({ ...prev, description: data.value }))}
                    rows={4}
                    style={{ 
                      minHeight: '100px',
                      fontSize: tokens.fontSizeBase300,
                      lineHeight: '1.5'
                    }}
                  />
                ) : (
                  <Text className={styles.descriptionField} style={{ 
                    padding: '12px 16px',
                    lineHeight: '1.6',
                    fontSize: tokens.fontSizeBase300,
                    minHeight: '60px'
                  }}>
                    {task.description || <span style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>No description provided</span>}
                  </Text>
                )}
              </div>
            </div>

            {/* Edit Actions */}
            {isEditing && (
              <div className={styles.editActions}>
                                 <Button
                   appearance="subtle"
                   icon={<DismissCircleRegular />}
                   onClick={handleCancelEdit}
                 >
                   Cancel
                 </Button>
                <Button
                  appearance="primary"
                  icon={<SaveRegular />}
                  onClick={() => {
                    console.log('🔍 TaskDetailsModal - Save button clicked!');
                    handleSave();
                  }}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}

            {/* Comments Section */}
            <div className={styles.commentsSection}>
              <div className={styles.commentsHeader}>
                <ChatRegular />
                <Text className={styles.sectionTitle}>Comments</Text>
                {loading && <Spinner size="tiny" />}
              </div>
              <TaskCommentThread
                taskId={task.id}
                comments={comments}
                onCommentAdded={(newComment) => setComments(prev => [newComment, ...prev])}
              />
            </div>

            {/* Footer Actions */}
            <div className={styles.actions}>
              <div>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  Task ID: {task.id}
                </Text>
              </div>
              <div className={styles.actionButtons}>
                <Button appearance="subtle" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default TaskDetailsModal; 